import {
  BarChart, Bar, XAxis, YAxis,
  Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { ListChecks } from 'lucide-react';
import type { FindingTypeRow } from '@/hooks/useTeamMonitoring';

// ─── Types ──────────────────────────────────────────────────────────────────

interface FindingTypeChartProps {
  data: FindingTypeRow[];
  loading?: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const CHART_COLORS = [
  'hsl(var(--primary))',
  'hsl(221 83% 58%)',
  'hsl(262 80% 60%)',
  'hsl(197 71% 53%)',
  'hsl(174 60% 45%)',
];

// ─── Skeleton ───────────────────────────────────────────────────────────────

function ChartSkeleton() {
  return (
    <div role="status" aria-label="Loading finding types chart" className="h-[200px] animate-pulse rounded bg-muted" />
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Finding Type frequency chart — FR-084.
 * Vertical bar chart of most frequent finding types (rule keys) across all jobs.
 * Shows top 8 finding types by count.
 * Admin-only (rendered inside admin-gated TeamMonitoringTab).
 *
 * WCAG: sr-only description enumerates top findings for screen readers.
 */
export function FindingTypeChart({ data, loading }: FindingTypeChartProps) {
  const topData = data.slice(0, 8).map((row, index) => ({
    ...row,
    fill: CHART_COLORS[index % CHART_COLORS.length],
  }));

  return (
    <section
      className="rounded-lg border border-border bg-card p-4 space-y-3"
      aria-label="Most frequent finding types"
    >
      <div className="flex items-center gap-2">
        <ListChecks className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h3 className="text-sm font-semibold text-foreground">Top Finding Types (FR-084)</h3>
      </div>

      {loading ? (
        <ChartSkeleton />
      ) : topData.length === 0 ? (
        <p className="text-xs text-muted-foreground italic py-8 text-center">No finding data yet.</p>
      ) : (
        <>
          <p className="sr-only">
            Bar chart of most frequent finding types.
            {topData.map((d) => `${d.label}: ${d.count} occurrences.`).join(' ')}
          </p>
          <div className="h-[200px]" aria-hidden="true">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topData} margin={{ left: 0, right: 8, top: 4, bottom: 32 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  angle={-35}
                  textAnchor="end"
                  interval={0}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={28}
                />
                <Tooltip
                  formatter={(value) => [value, 'Occurrences']}
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '6px',
                    fontSize: 12,
                  }}
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </>
      )}
    </section>
  );
}
